export const CODE_PRACTICE_LANGUAGES = [
  {
    slug: "javascript",
    label: "JavaScript",
    monacoLanguage: "javascript",
    accent: "#f59e0b",
    glow: "rgba(245, 158, 11, 0.2)",
    description:
      "Practice arrays, objects, loops, and interview-style logic with instant in-browser run support.",
    runMode: "browser",
    starterCode: `function secondLargest(nums) {
  // return the second largest unique number
}

const sample = [4, 8, 2, 8, 6, 1];
console.log(secondLargest(sample));`,
    tips: [
      "Prefer small helper functions for repeated logic.",
      "Log intermediate values when your condition chain gets confusing.",
      "Solve for clarity first, then optimize.",
    ],
    drills: [
      {
        title: "Two Sum",
        prompt:
          "Given an array of integers and a target, return the indices of the two numbers that add up to the target.",
        exampleInput: "nums = [2, 7, 11, 15], target = 9",
        exampleOutput: "[0, 1]",
      },
      {
        title: "Valid Parentheses",
        prompt:
          "Check whether an input string has valid opening and closing brackets using a stack-based approach.",
        exampleInput: `"()[]{}"`,
        exampleOutput: "true",
      },
      {
        title: "Maximum Subarray",
        prompt:
          "Return the maximum possible sum of a contiguous subarray using an efficient approach.",
        exampleInput: "[-2,1,-3,4,-1,2,1,-5,4]",
        exampleOutput: "6",
      },
      {
        title: "Second largest unique number",
        prompt:
          "Given an array of numbers, return the second largest unique number. If it does not exist, return null.",
        exampleInput: "[4, 8, 2, 8, 6, 1]",
        exampleOutput: "6",
      },
      {
        title: "Frequency map challenge",
        prompt:
          "Count character frequency in a string and return the most frequent character with its count.",
        exampleInput: `"skillldev"`,
        exampleOutput: `{ char: "l", count: 3 }`,
      },
      {
        title: "Group students by score band",
        prompt:
          "Group students into high, medium, and low score bands using array methods and clean condition handling.",
        exampleInput: `[{ name: "A", score: 92 }, { name: "B", score: 68 }]`,
        exampleOutput: `{ high: ["A"], medium: [], low: ["B"] }`,
      },
    ],
  },
  {
    slug: "python",
    label: "Python",
    monacoLanguage: "python",
    accent: "#38bdf8",
    glow: "rgba(56, 189, 248, 0.2)",
    description:
      "Use Python for clean logic building, interview drills, maps, sets, and readable problem-solving flow.",
    runMode: "draft",
    starterCode: `def longest_streak(nums):
    # return longest consecutive streak length
    pass

sample = [100, 4, 200, 1, 3, 2]
print(longest_streak(sample))`,
    tips: [
      "Use sets for O(1) existence checks in sequence problems.",
      "Write down the base case before nested loops.",
      "Keep names readable so your future self can debug faster.",
    ],
    drills: [
      {
        title: "Two Sum",
        prompt:
          "Return the indices of two numbers that add up to the target using a hash map.",
        exampleInput: "nums = [2, 7, 11, 15], target = 9",
        exampleOutput: "[0, 1]",
      },
      {
        title: "Longest Substring Without Repeating Characters",
        prompt:
          "Use a sliding window to find the length of the longest substring without repeated characters.",
        exampleInput: `"abcabcbb"`,
        exampleOutput: "3",
      },
      {
        title: "Valid Anagram",
        prompt:
          "Return true if one string is an anagram of another, otherwise false.",
        exampleInput: `s = "anagram", t = "nagaram"`,
        exampleOutput: "true",
      },
      {
        title: "Longest consecutive streak",
        prompt:
          "Return the length of the longest consecutive sequence in an unsorted array.",
        exampleInput: "[100, 4, 200, 1, 3, 2]",
        exampleOutput: "4",
      },
      {
        title: "Top k words",
        prompt:
          "Find the top K frequent words and return them in frequency order.",
        exampleInput: `["roadmap", "ai", "roadmap", "focus", "ai", "ai"]`,
        exampleOutput: `["ai", "roadmap"]`,
      },
      {
        title: "Merge intervals",
        prompt:
          "Merge overlapping intervals and return the smallest clean interval list.",
        exampleInput: "[[1, 3], [2, 6], [8, 10], [15, 18]]",
        exampleOutput: "[[1, 6], [8, 10], [15, 18]]",
      },
    ],
  },
  {
    slug: "java",
    label: "Java",
    monacoLanguage: "java",
    accent: "#f97316",
    glow: "rgba(249, 115, 22, 0.2)",
    description:
      "Practice object-oriented logic, DSA structure, and interview questions with strong method design.",
    runMode: "draft",
    starterCode: `import java.util.*;

public class Main {
  static boolean isPalindrome(String value) {
    // write logic here
    return false;
  }

  public static void main(String[] args) {
    System.out.println(isPalindrome("level"));
  }
}`,
    tips: [
      "Separate data preparation from core comparison logic.",
      "Use helper methods when a single method starts doing too much.",
      "Think in edge cases before you code loops.",
    ],
    drills: [
      {
        title: "Two Sum",
        prompt:
          "Solve the classic two-sum problem using a HashMap and return the matching indices.",
        exampleInput: "nums = [2, 7, 11, 15], target = 9",
        exampleOutput: "[0, 1]",
      },
      {
        title: "Valid Parentheses",
        prompt:
          "Use a stack to verify whether a string of brackets is balanced.",
        exampleInput: `"({[]})"`,
        exampleOutput: "true",
      },
      {
        title: "Merge Two Sorted Lists",
        prompt:
          "Merge two sorted linked lists and return one sorted list.",
        exampleInput: "list1 = [1,2,4], list2 = [1,3,4]",
        exampleOutput: "[1,1,2,3,4,4]",
      },
      {
        title: "Palindrome after cleanup",
        prompt:
          "Check whether a string is a palindrome after removing spaces and special characters.",
        exampleInput: `"A man, a plan, a canal: Panama"`,
        exampleOutput: "true",
      },
      {
        title: "Student leaderboard",
        prompt:
          "Sort a student list by score descending, then by name ascending if scores match.",
        exampleInput: `[(Riya, 80), (Aman, 92), (Asha, 92)]`,
        exampleOutput: `[(Aman, 92), (Asha, 92), (Riya, 80)]`,
      },
      {
        title: "Balanced brackets",
        prompt:
          "Use a stack to validate whether all brackets are balanced in an expression.",
        exampleInput: `"{[()()]}"`,
        exampleOutput: "true",
      },
    ],
  },
  {
    slug: "cpp",
    label: "C++",
    monacoLanguage: "cpp",
    accent: "#a78bfa",
    glow: "rgba(167, 139, 250, 0.2)",
    description:
      "Sharpen DSA thinking, pointers, arrays, and competitive-programming style logic in C++.",
    runMode: "draft",
    starterCode: `#include <bits/stdc++.h>
using namespace std;

int maxProfit(vector<int>& prices) {
  // return best buy-sell profit
  return 0;
}

int main() {
  vector<int> prices = {7, 1, 5, 3, 6, 4};
  cout << maxProfit(prices) << endl;
}`,
    tips: [
      "Track time complexity before using nested loops by habit.",
      "Use small dry runs on paper when index logic gets tricky.",
      "Prefer descriptive variable names over one-letter shortcuts while learning.",
    ],
    drills: [
      {
        title: "Two Sum",
        prompt:
          "Return the indices of the two numbers that add up to a target using an efficient hash-based approach.",
        exampleInput: "nums = [2, 7, 11, 15], target = 9",
        exampleOutput: "[0, 1]",
      },
      {
        title: "Valid Parentheses",
        prompt:
          "Check whether brackets are balanced using a stack in competitive-programming style.",
        exampleInput: `"{[()()]}"`,
        exampleOutput: "true",
      },
      {
        title: "Maximum Subarray",
        prompt:
          "Return the maximum sum of any contiguous subarray using Kadane's algorithm.",
        exampleInput: "[-2,1,-3,4,-1,2,1,-5,4]",
        exampleOutput: "6",
      },
      {
        title: "Best time to buy and sell stock",
        prompt:
          "Given stock prices, return the maximum profit from a single buy and a single sell.",
        exampleInput: "[7, 1, 5, 3, 6, 4]",
        exampleOutput: "5",
      },
      {
        title: "Two sum with indices",
        prompt:
          "Return the indices of two numbers that add up to a target using an efficient approach.",
        exampleInput: "nums = [2, 7, 11, 15], target = 9",
        exampleOutput: "[0, 1]",
      },
      {
        title: "Sliding window maximum sum",
        prompt:
          "Find the maximum sum of any subarray of length k using a sliding window.",
        exampleInput: "arr = [2, 1, 5, 1, 3, 2], k = 3",
        exampleOutput: "9",
      },
    ],
  },
  {
    slug: "go",
    label: "Go",
    monacoLanguage: "go",
    accent: "#22c55e",
    glow: "rgba(34, 197, 94, 0.18)",
    description:
      "Practice backend-friendly logic, maps, slices, and clean function design with Go fundamentals.",
    runMode: "draft",
    starterCode: `package main

import "fmt"

func hasDuplicates(nums []int) bool {
  // write logic here
  return false
}

func main() {
  fmt.Println(hasDuplicates([]int{1, 2, 3, 1}))
}`,
    tips: [
      "Use maps when you need fast lookups and state tracking.",
      "Keep functions small and testable.",
      "Return early to avoid deeply nested condition blocks.",
    ],
    drills: [
      {
        title: "Two Sum",
        prompt:
          "Solve the two-sum problem using a map and return the pair indices.",
        exampleInput: "nums = [2, 7, 11, 15], target = 9",
        exampleOutput: "[0, 1]",
      },
      {
        title: "Contains Duplicate",
        prompt:
          "Return true if any value appears more than once in the slice.",
        exampleInput: "[1, 2, 3, 1]",
        exampleOutput: "true",
      },
      {
        title: "Valid Anagram",
        prompt:
          "Check whether two strings are anagrams using maps and clean counting logic.",
        exampleInput: `s = "listen", t = "silent"`,
        exampleOutput: "true",
      },
      {
        title: "Contains duplicate",
        prompt:
          "Return true if any value appears at least twice in the array.",
        exampleInput: "[1, 2, 3, 1]",
        exampleOutput: "true",
      },
      {
        title: "Task grouping by status",
        prompt:
          "Group tasks by status and return a map with slices of task names.",
        exampleInput: `[{title:"A", status:"todo"}, {title:"B", status:"done"}]`,
        exampleOutput: `map[todo:[A] done:[B]]`,
      },
      {
        title: "Reverse words",
        prompt:
          "Reverse words in a sentence while removing extra spaces.",
        exampleInput: `"  code   with   clarity  "`,
        exampleOutput: `"clarity with code"`,
      },
    ],
  },
];

export const getCodePracticeLanguage = (slug) =>
  CODE_PRACTICE_LANGUAGES.find((item) => item.slug === slug);
